/**
 * Row action assignment for the balance table (gdn-2979).
 *
 * Every row gets at most ONE primary button — the verb the biller most
 * likely wants next — and the remaining eligible verbs move to a "…"
 * overflow menu. This replaces the "Apply credit supersedes Review
 * credit" compromise: nothing is hidden anymore, secondary verbs are just
 * one click further away. Future verbs (Refund, Assign to visit)
 * are one entry here, not a layout renegotiation.
 *
 * Pure derivation over rows.ts eligibility (creditSourceOf et al. stay
 * the single authority on WHO qualifies; this module only decides WHERE
 * a qualifying verb renders).
 */

import {
  type BalanceTableRow,
  type CreditSource,
  formatCents,
  heldChargeOf,
} from './rows';

/** Labels for the reconciliation's recommended_action verbs. */
export const ROW_ACTION_LABELS: Record<string, string> = {
  send_invoice: 'Send invoice',
  send_reminder: 'Send reminder',
  review_credit_or_refund: 'Review credit',
  suspend_invoice_wait_secondary: 'Review balances',
  review_denial: 'Review claim',
};

/** The account row's reminder rides an invoice, not a reconciliation —
 * distinct key so the table can route it to the invoice handler. */
/**
 * Recommendations that stop being buttons (gdn-2957).
 *
 * `review_credit_or_refund` existed because the ledger drawer was the only
 * place to see credit detail. Apply credit and Refund now sit on the same
 * row, so it offered a third control meaning "go and look at the credit
 * you are already looking at".
 *
 * Dropping it can't strand the row: both producers only emit it with
 * credit_cents > 0 on an over-collected (non-hold) encounter, so
 * creditEligibilityOf returns `eligible` and BOTH credit verbs are pushed
 * live — not blocked, live. See the actions test for that invariant.
 */
const DROPPED_RECOMMENDATIONS = new Set(['review_credit_or_refund']);

/** Recommended actions that bill the patient, and so ride the money
 * capability (gdn-3287). The rest — Review balances, Review claim — are
 * navigation to something the biller may look at without being allowed
 * to act on it, so they stay: withholding them would hide the ledger,
 * not the authority. */
const MONEY_RECOMMENDATIONS = new Set(['send_invoice', 'send_reminder']);

/**
 * Recommendations that are a status rather than an act (gdn-2957).
 *
 * `suspend_invoice_wait_secondary` means "secondary hasn't adjudicated —
 * don't collect yet". It was shaped like a verb only because the drawer
 * was somewhere to send the user.
 *
 * It is converted to a blocked entry rather than removed. Its row is a
 * hold, and holds return `not_applicable` from creditEligibilityOf — by
 * design, since a held balance is provisional and its credit must not
 * move — so both credit verbs contribute nothing. Remove this too and the
 * row has zero candidates, which renders an entirely blank actions cell
 * (the "…" is gated on menu.length). A held row that happens to carry an
 * invoice would still show a "…" for Copy link / Void, so identical states
 * would get different chrome for a reason invisible to the biller. Saying
 * why there is nothing to do beats saying nothing.
 */
const STATUS_RECOMMENDATIONS: Record<string, { label: string; reason: string }> = {
  suspend_invoice_wait_secondary: {
    label: 'Waiting on secondary',
    reason: "Secondary hasn't adjudicated yet — don't collect from the patient.",
  },
};

export const ACCOUNT_REMINDER_KEY = 'account_send_reminder';
/** The Open Invoices panel's verbs, moved onto the row that carries the
 * invoice (gdn-3001). Never primary — the row's money verb outranks
 * housekeeping on the invoice itself. */
export const INVOICE_COPY_LINK_KEY = 'invoice_copy_link';
export const INVOICE_VOID_KEY = 'invoice_void';
/** The declined-invoice recovery verbs (gdn-3064 / gdn-3061). Retry leads
 * with card selection — re-presenting a card the issuer already refused
 * fails again, and repeated authorisations against a failing card are what
 * issuers penalise — hence the label. Stop-retries switches off Stripe's
 * automatic collection WITHOUT touching the debt; it must never be
 * labelled anywhere near voiding, which kills the invoice (the debt
 * survives, released back to uninvoiced). */
export const INVOICE_RETRY_PAYMENT_KEY = 'invoice_retry_payment';
export const INVOICE_STOP_RETRIES_KEY = 'invoice_stop_retries';
/** Repair a stranded charge by moving its money to the encounter it was
 * really for (gdn-3106). Offered only where the server will accept it —
 * see assignableChargeOf — and never primary: the row's own money verbs
 * outrank a correction to how an old payment was filed. */
export const ASSIGN_CHARGE_KEY = 'assign_charge_to_encounter';
export const APPLY_CREDIT_KEY = 'apply_credit';
export const RECORD_REFUND_KEY = 'record_refund';
export const REVERSE_CHARGE_KEY = 'reverse_charge_line';
export const REMOVE_RECORDED_PAYMENT_KEY = 'remove_recorded_payment';
/** The biller's two answers to a charge a refund left held (gdn-4242). */
export const BILL_HELD_CHARGE_KEY = 'bill_held_charge';
export const WRITE_OFF_HELD_CHARGE_KEY = 'write_off_held_charge';

export interface RowAction {
  /** Stable verb key — doubles as the analytics `action` value. */
  key: string;
  label: string;
  /** Amount-bearing label for AT users where the visible label omits it. */
  ariaLabel?: string;
  /** Set when the verb applies to this row but cannot run right now. The
   * item still renders — silently dropping it left the biller asking
   * where the button went (gdn-2994) — disabled, carrying this reason. */
  blockedReason?: string;
  /** Housekeeping that must never lead the row. Being pushed last is not
   * enough: a row can carry an invoice while having no money verb at all
   * (an encounter with linked_invoice_ids but no reconciliation), and then
   * last is also first. Declared on the verb so the rule survives a third
   * housekeeping action being added. */
  neverPrimary?: boolean;
}

export interface RowActions {
  /** The recommended verb, promoted out of the menu. Never a blocked
   * verb — a disabled primary button would be a dead end — and never
   * invoice housekeeping, which stays in the menu even on a row that
   * offers nothing else. */
  primary: RowAction | null;
  /** Secondary verbs — everything except the primary, blocked ones
   * included. */
  overflow: RowAction[];
  /** Everything this row can do, in order, blocked verbs and the primary
   * included: "the … lists every action; the button is the recommended
   * one promoted" is a simpler model to hold than "the … lists
   * everything except the button" (gdn-2994). Empty means the row shows
   * no "…" at all. */
  menu: RowAction[];
}

export interface RowActionsContext {
  /** The row's eligible allocation source (creditSourceOf composed with
   * the "somewhere for the money to go" target check), or null. */
  applyCredit: CreditSource | null;
  /** Why Apply credit cannot run, when it applies to this row but is
   * blocked — the row holds credit none of which is movable, or nothing
   * owes. Ignored when `applyCredit` is set. */
  applyCreditBlockedReason?: string | null;
  /** The row's eligible refund source (creditSourceOf without the target
   * check — a refund needs credit, not a destination visit), or null.
   * Hold-state exclusion is inherited from creditSourceOf. */
  recordRefund?: CreditSource | null;
  /** Why Refund cannot run, when it applies but is blocked. */
  recordRefundBlockedReason?: string | null;
  /** The account row's send-reminder verb is wired (row has an invoice
   * and the surface supplied a reminder handler). */
  accountReminderAvailable?: boolean;
  /** The surface supplied a copy-invoice-link handler. */
  invoiceCopyLinkAvailable?: boolean;
  /** The surface supplied a void-invoice handler. */
  invoiceVoidAvailable?: boolean;
  /** The surface supplied an update-card-and-retry handler (gdn-3064). */
  invoiceRetryPaymentAvailable?: boolean;
  /** The surface supplied a stop-auto-collection handler (gdn-3064). */
  invoiceStopRetriesAvailable?: boolean;
  /** The surface supplied an assign-charge handler AND the row is an
   * assignable stranded charge (gdn-3106). */
  assignChargeAvailable?: boolean;
  /** The surface supplied a reverse handler AND the row is backed by a
   * single charge line (gdn-3187). */
  reverseChargeAvailable?: boolean;
  /** The surface supplied a remove handler AND the credit row has at
   * least one PAYMENT source to remove (gdn-3255). */
  removeRecordedPaymentAvailable?: boolean;
  /** The surface supplied the Bill patient and Write off handlers for a
   * charge held after a refund (gdn-4242). Whether THIS row is held is
   * read from the row itself (heldChargeOf). */
  heldChargeDecisionAvailable?: boolean;
  /** Subject language, lower case ("patient" | "client"), for the verbs
   * that name the person. Defaults to "patient". */
  subject?: string;
  /** Whether the viewer may act on this patient's money (gdn-3287).
   * Suppresses the billing recommendations — Send invoice, Send reminder
   * — while leaving the review ones, which are navigation rather than
   * authority. Defaults true so existing callers are unchanged. */
  canManagePatientFinancials?: boolean;
}

/**
 * Assign a row's eligible verbs to {primary, overflow}.
 *
 * Priority (first eligible wins primary, the rest overflow in order):
 * - encounter rows: Apply credit, Refund, then Update card & retry when
 *   the invoice's decline has no retries left, then the recommended
 *   action;
 * - account rows: Apply credit, Refund, the same exhausted-decline retry,
 *   then Send reminder.
 * Any row riding an invoice also carries that invoice's housekeeping
 * (copy link, void — plus, on a decline with retries still pending, the
 * deliberately quiet retry and stop-retries verbs) at the end of the
 * overflow, never as primary.
 *
 * The decline split (gdn-3064): Stripe's dunning recovers roughly nine
 * declines in ten unattended, so while retries are pending the correct
 * biller action is usually NOTHING — the recovery verbs exist but stay
 * one click away, because a prominent chase button there generates work
 * that costs money to do. Once Stripe stops, the row is in the one-in-ten
 * that will not fix itself, and Update card & retry may lead the row.
 *
 * Assumes the table is mounted with both credit handlers: without them
 * applyCreditFor/recordRefundFor yield neither a source nor a reason, so
 * those verbs are absent rather than blocked.
 *
 * Apply credit outranks the recommended action deliberately: where both
 * qualify the recommendation is "review_credit_or_refund", and applying
 * the credit IS the resolution short of a refund (gdn-2950) — review
 * stays one click away in the overflow instead of disappearing. Record
 * refund is always an overflow verb, never the primary (gdn-4178). A
 * credit row still always has a resolution path (gdn-2978): Refund stays
 * in its menu, one deliberate click away.
 */
/**
 * NOTE: a surface that mounts the table without `onApplyCredit` /
 * `onRecordRefund` gets `{source: null, blockedReason: null}` from both
 * credit verbs, which would leave an over-collected row with no candidates
 * now that `review_credit_or_refund` no longer backstops it.
 * PatientResponsibilityPanel is the only caller today and passes both;
 * anything new mounting this table must too.
 */
export const rowActionsOf = (
  row: BalanceTableRow,
  ctx: RowActionsContext,
): RowActions => {
  const candidates: RowAction[] = [];

  if (ctx.applyCredit) {
    // The most it can apply anywhere: a visit's cap, or the larger cap for
    // the account's own charges (gdn-4321).
    const applicableCents = Math.max(
      ctx.applyCredit.creditCents,
      ctx.applyCredit.chargeCreditCents ?? 0,
    );
    candidates.push({
      key: APPLY_CREDIT_KEY,
      label: 'Apply credit',
      ariaLabel:
        applicableCents > 0
          ? `Apply credit of ${formatCents(applicableCents)}`
          : 'Apply credit',
    });
  } else if (ctx.applyCreditBlockedReason) {
    candidates.push({
      key: APPLY_CREDIT_KEY,
      label: 'Apply credit',
      blockedReason: ctx.applyCreditBlockedReason,
    });
  }
  if (ctx.recordRefund) {
    candidates.push({
      key: RECORD_REFUND_KEY,
      label: 'Refund',
      ariaLabel:
        ctx.recordRefund.creditCents > 0
          ? `Refund up to ${formatCents(ctx.recordRefund.creditCents)}`
          : 'Refund',
      // Never primary (gdn-4178). It became the row's main button whenever
      // Apply credit had nowhere to go, which is exactly when the credit is
      // least likely to be what it looks like. At Island Life that meant a
      // visit's credit sitting next to an unpaid account-level fee
      // (refunding the card leaves the fee owed), or a payment recorded
      // twice (refunding pays out money that never came in). A refund
      // sends real money back, so like reverse it waits one click away.
      neverPrimary: true,
    });
  } else if (ctx.recordRefundBlockedReason) {
    candidates.push({
      key: RECORD_REFUND_KEY,
      label: 'Refund',
      blockedReason: ctx.recordRefundBlockedReason,
    });
  }

  // A charge a refund left held (gdn-4242): the row's business is the
  // biller's decision, so Bill patient leads and Write off, the other
  // answer, waits one click away.
  const held = heldChargeOf(row) !== null;
  if (held && ctx.heldChargeDecisionAvailable) {
    candidates.push({ key: BILL_HELD_CHARGE_KEY, label: `Bill ${ctx.subject ?? 'patient'}` });
    candidates.push({ key: WRITE_OFF_HELD_CHARGE_KEY, label: 'Write off', neverPrimary: true });
  }

  const decline = row.invoice?.decline ?? null;
  const retriesExhausted = decline !== null && decline.nextAttemptDate === null;

  // Exhausted decline: the invoice will not collect on its own, so the
  // recovery verb outranks the recommended action (which on such a row is
  // typically send_reminder — chasing a person whose card simply needs
  // replacing).
  if (decline && retriesExhausted && ctx.invoiceRetryPaymentAvailable) {
    candidates.push({
      key: INVOICE_RETRY_PAYMENT_KEY,
      label: 'Update card & retry',
    });
  }

  if (row.kind === 'encounter') {
    const recommended = row.reconciliation?.recommended_action;
    const status = recommended ? STATUS_RECOMMENDATIONS[recommended] : undefined;
    const label = recommended ? ROW_ACTION_LABELS[recommended] : undefined;
    if (recommended && status) {
      candidates.push({
        key: recommended,
        label: status.label,
        blockedReason: status.reason,
      });
    } else if (
      recommended &&
      label &&
      !DROPPED_RECOMMENDATIONS.has(recommended) &&
      (ctx.canManagePatientFinancials !== false || !MONEY_RECOMMENDATIONS.has(recommended))
    ) {
      candidates.push({ key: recommended, label });
    }
  } else if (row.invoice && ctx.accountReminderAvailable && !held) {
    // Never on a held charge: nothing may collect it until the biller
    // decides, and a reminder would ask the patient for exactly that.
    candidates.push({ key: ACCOUNT_REMINDER_KEY, label: 'Send reminder' });
  }

  // A settled charge filed against the account instead of the visit it
  // paid for. Never primary: the row is already settled, so this competes
  // with nothing urgent, and a repair verb leading a quiet row would read
  // as an alarm about money that is fine.
  if (ctx.assignChargeAvailable) {
    candidates.push({
      key: ASSIGN_CHARGE_KEY,
      label: 'Assign to encounter',
      neverPrimary: true,
    });
  }

  // Correcting a payment that was never received (gdn-3255). Never
  // primary for the same reason as reverse, and one more: this row's
  // business is the credit sitting on it, and Apply credit is what a
  // biller usually wants here. A correction verb leading the row would
  // suggest the credit is suspect whenever it appears.
  if (ctx.removeRecordedPaymentAvailable) {
    candidates.push({
      key: REMOVE_RECORDED_PAYMENT_KEY,
      label: 'Remove recorded payment…',
      neverPrimary: true,
    });
  }

  // Undoing a charge that should not have been raised (gdn-3187). Never
  // primary, and for a stronger reason than the verbs above: this one is
  // destructive. A row's expected business is collecting what it says is
  // owed; a correction belongs one deliberate click away, not where the
  // eye lands. Offered coarsely — the server refuses ineligible lines
  // with a stated reason, and a missing button states nothing.
  //
  // Placed before the invoice block: this is a verb ABOUT the row's own
  // charge, not housekeeping on an invoice the row happens to ride.
  if (ctx.reverseChargeAvailable) {
    candidates.push({
      key: REVERSE_CHARGE_KEY,
      label: 'Reverse charge',
      neverPrimary: true,
    });
  }

  // Housekeeping on the invoice itself, for any row riding one. These used
  // to live in the Open Invoices panel's overflow; the panel is gone under
  // the gate, so they ride the row instead of disappearing.
  if (row.invoice) {
    // Pending decline: Stripe very probably recovers this on its own, so
    // both recovery verbs are deliberately quiet — present for the biller
    // who knows the card is dead, never leading the row.
    if (decline && !retriesExhausted && ctx.invoiceRetryPaymentAvailable) {
      candidates.push({
        key: INVOICE_RETRY_PAYMENT_KEY,
        label: 'Update card & retry',
        neverPrimary: true,
      });
    }
    // The deliberate opt-OUT of collection that works nine times in ten —
    // only offered while there is something left to stop. Stops the
    // chasing, leaves the debt: never to be confused with voiding.
    if (decline && !retriesExhausted && ctx.invoiceStopRetriesAvailable) {
      candidates.push({
        key: INVOICE_STOP_RETRIES_KEY,
        label: 'Stop retrying this card',
        neverPrimary: true,
      });
    }
    if (ctx.invoiceCopyLinkAvailable) {
      candidates.push({
        key: INVOICE_COPY_LINK_KEY,
        label: 'Copy invoice link',
        neverPrimary: true,
      });
    }
    if (ctx.invoiceVoidAvailable) {
      candidates.push({
        key: INVOICE_VOID_KEY,
        label: 'Void invoice',
        neverPrimary: true,
      });
    }
  }

  // A blocked verb can never be the primary button: promoting a dead
  // control is worse than leaving the slot to the next runnable verb.
  // Nor can invoice housekeeping — voiding an invoice is not what a row
  // is for, however little else it offers.
  const primary =
    candidates.find(action => !action.blockedReason && !action.neverPrimary) ?? null;
  return {
    primary,
    overflow: candidates.filter(action => action !== primary),
    menu: candidates,
  };
};
